import { useEffect, useState } from "react";
import { Download, FileText, Lock } from "lucide-react";
import { api } from "@/lib/ppcp";
import { DOWNLOADS } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Printable A4 editions, built from the same content as the screens. Locked documents stay
 *  visible with an upgrade link, exactly like the Complete Meal Plan tiles. */
export const DownloadsPanel = ({ slug, upgradeUrl, nativeUpgrade = false }) => {
  const [documents, setDocuments] = useState(null);

  useEffect(() => {
    api
      .get(`/programs/${slug}/documents`)
      .then((res) => setDocuments(res.data.documents ?? []))
      .catch(() => setDocuments([]));
  }, [slug]);

  if (!documents?.length) return null;

  return (
    <section data-testid={DOWNLOADS.panel} className="rounded-[22px] border border-brand-teal/10 bg-white p-5 shadow-[0_1px_2px_rgba(15,46,44,0.04),0_10px_24px_-14px_rgba(15,46,44,0.18)] sm:p-6">
      <p className="text-[11.5px] font-bold uppercase tracking-[0.09em] text-brand-orangeInk">
        Take it with you
      </p>
      <div className="mb-4 mt-2">
        <h2 className="font-display text-[19px] font-bold leading-snug text-brand-teal sm:text-[21px]">
          Printable PDFs
        </h2>
        <p className="mt-1.5 text-[13.5px] leading-[1.6] text-brand-teal/60">
          A4, ready to print or keep on your phone — the original designed editions of your plan and
          guides, exactly as published.
        </p>
      </div>

      <ul className="grid gap-2.5">
        {documents.map((doc) =>
          doc.unlocked ? (
            <li key={doc.key}>
              <a
                data-testid={DOWNLOADS.item(doc.key)}
                href={`${BACKEND_URL}${doc.download_path}`}
                target="_blank"
                rel="noopener"
                className="ps-tap group flex w-full items-center gap-3.5 rounded-[16px] bg-brand-mintSoft px-4 py-3.5 text-left transition-colors hover:bg-brand-tealTint"
              >
                <span className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-[11px] bg-white">
                  <FileText className="h-[18px] w-[18px] text-brand-tealSoft" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-bold text-brand-teal">{doc.title}</span>
                  <span className="mt-0.5 block text-[12px] text-brand-teal/60">
                    {doc.description}
                    {doc.pages && !doc.description?.includes("pages") ? ` · ${doc.pages} pages` : ""}
                  </span>
                </span>
                <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-brand-tealDeep text-white transition-transform duration-300 group-hover:translate-y-0.5">
                  <Download className="h-4 w-4" />
                </span>
              </a>
            </li>
          ) : (
            <li
              key={doc.key}
              data-testid={DOWNLOADS.locked(doc.key)}
              className="flex items-center gap-3.5 rounded-[16px] bg-brand-mintSoft px-4 py-3.5"
            >
              <span className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-[11px] bg-white">
                <Lock className="h-4 w-4 text-brand-teal/35" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-bold text-brand-teal">{doc.title}</span>
                <span className="mt-0.5 block text-[12px] text-brand-teal/60">
                  This PDF is part of the Complete Meal Plan.
                </span>
              </span>
              {upgradeUrl && (
                <a
                  href={upgradeUrl}
                  target="_blank"
                  rel="noopener"
                  data-testid={`upgrade-pdf-${doc.key}`}
                  className="ps-tap shrink-0 text-right text-[11px] font-bold text-brand-orangeInk hover:underline"
                >
                  <span className="mb-0.5 block text-[10px] uppercase tracking-[0.03em] text-brand-teal/40">
                    Included with
                  </span>
                  {nativeUpgrade ? "Upgrade to Complete Plan →" : "Complete Meal Plan → Unlock"}
                </a>
              )}
            </li>
          )
        )}
      </ul>
    </section>
  );
};
